/**
 * TYPES
 */
import {IKanban} from 'src/aggregates/user/queries.d';
import {IKanbanByTag} from 'src/aggregates/user/state.d';
import {IKanbanReconciliation}
    from 'src/aggregates/user/subscriptions/reconcile.d';


/**
 * CODE
 */

/**
 * I reconcile the columns received on kanban-updated event with the stored
 * kanban metadata.
 *
 * A new column starts with default metadata and its tag is marked to
 * subscribe, an existing column preserves the stored metadata and a removed
 * column is left out of the metadata and its tag is marked to unsubscribe.
 *
 * :param columns: kanban columns received on the event
 * :param storedByTag: stored kanban metadata by tag
 *
 * :returns: reconciled metadata and tags to subscribe and to unsubscribe
 */
function reduceKanbanUpdated(
    columns: IKanban[] | null | undefined,
    storedByTag: Record<number, IKanbanByTag> | null | undefined,
): IKanbanReconciliation
{
    // normalize stored metadata
    const stored = storedByTag ?? {};

    // no columns data: return stored metadata untouched
    if (columns === null || columns === undefined)
    {
        return {
            byTag: stored,
            tagsToSubscribe: [],
            tagsToUnsubscribe: [],
        };
    }

    // initialize reconciled metadata and tags to subscribe
    const byTag: Record<number, IKanbanByTag> = {};
    const tagsToSubscribe: number[] = [];

    // reduce received columns
    for (const column of columns)
    {
        // column has no tag: skip incomplete column
        if (typeof column?.tag !== 'number')
        {
            continue;
        }

        // get stored column metadata
        const storedColumn = stored[column.tag];

        // column is new: mark its tag to subscribe
        if (storedColumn === undefined)
        {
            tagsToSubscribe.push(column.tag);
        }

        // set column metadata preserving stored data
        byTag[column.tag] = {
            columnName: column.name,
            customers: storedColumn?.customers ?? [],
            hasError: storedColumn?.hasError ?? false,
            hasMore: storedColumn?.hasMore ?? true,
            loading: storedColumn?.loading ?? false,
            page: storedColumn?.page ?? 0,
            prio: column.prio,
            tag: column.tag,
        };
    }

    // reduce removed columns tags to unsubscribe
    const tagsToUnsubscribe = Object.keys(stored)
        .map(tag => Number(tag))
        .filter(tag => byTag[tag] === undefined);

    // return reconciliation
    return {
        byTag,
        tagsToSubscribe,
        tagsToUnsubscribe,
    };
}


/**
 * EXPORTS
 */
export {
    reduceKanbanUpdated,
};
